import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {Form, Field} from 'react-final-form';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Box,
  Grid,
  LoadingButton,
  TextInput,
  useMediaQuery,
} from '@tenancy.nz/design-system';
import UploadButton from './UploadButton';
import {
  env,
  getFieldProps,
  composeValidators,
  isEmailAddress,
  isRequired,
} from '../utils/helpers';

function ContactForm({
  attachments,
  initialValues,
  onAttachmentRemove,
  onAttachmentUpload,
  onSubmit,
  ...rest
}) {
  const reCaptchaRef = useRef();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const handleOnSubmit = async values => {
    if (reCaptchaRef.current.getValue()) {
      reCaptchaRef.current.reset();
    }
    const recaptcha = await reCaptchaRef.current.executeAsync();
    return onSubmit({...values, recaptcha});
  };

  return (
    <Form initialValues={initialValues} onSubmit={handleOnSubmit} {...rest}>
      {({handleSubmit, submitting, pristine, form}) => (
        <form
          onSubmit={e => {
            handleSubmit(e).then(() => {
              form.restart();
            });
          }}>
          <Box my={3}>
            <Grid container spacing={5}>
              <Grid item xs={12} sm={6}>
                <Field name="first_name" validate={isRequired}>
                  {({input, meta}) => (
                    <TextInput
                      {...getFieldProps({input, meta})}
                      required
                      fullWidth
                      label="First Name"
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field name="last_name" validate={isRequired}>
                  {({input, meta}) => (
                    <TextInput
                      {...getFieldProps({input, meta})}
                      required
                      fullWidth
                      label="Last Name"
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="email"
                  validate={composeValidators(isRequired, isEmailAddress)}>
                  {({input, meta}) => (
                    <TextInput
                      {...getFieldProps({input, meta})}
                      required
                      fullWidth
                      label="Email"
                      type="email"
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field name="phone" validate={isRequired}>
                  {({input, meta}) => (
                    <TextInput
                      {...getFieldProps({input, meta})}
                      required
                      fullWidth
                      label="Phone"
                      type="tel"
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12}>
                <Field name="subject" validate={isRequired}>
                  {({input, meta}) => (
                    <TextInput
                      {...getFieldProps({input, meta})}
                      required
                      fullWidth
                      label="Subject"
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12}>
                <UploadButton
                  name="attachments"
                  files={attachments}
                  helperText="Eg. Screenshots of issue/error including the full URL (Max 3 files, 5mb each)"
                  maxFiles={3}
                  onUpload={onAttachmentUpload}
                  color="primary"
                  size="small"
                  onRemove={onAttachmentRemove}>
                  Upload Attachments
                </UploadButton>
              </Grid>
              <Grid item xs={12}>
                <Field name="comments" validate={isRequired}>
                  {({input, meta}) => (
                    <TextInput
                      {...getFieldProps({input, meta})}
                      required
                      fullWidth
                      label="Message"
                      multiline
                      rows={8}
                      helperText="Please provide the full details of the issue/error you are facing along with the page URL."
                      helperTextPlacement="below"
                    />
                  )}
                </Field>
                <ReCAPTCHA
                  sitekey={env('REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY')}
                  ref={reCaptchaRef}
                  size="invisible"
                />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton
                  type="submit"
                  fill={isMobile}
                  color="primary"
                  loading
                  pending={submitting}
                  disabled={pristine}>
                  Send Message
                </LoadingButton>
              </Grid>
            </Grid>
          </Box>
        </form>
      )}
    </Form>
  );
}

ContactForm.propTypes = {
  /**
   *
   */
  attachments: PropTypes.arrayOf(PropTypes.shape({})),

  /**
   *
   */
  initialValues: PropTypes.shape({}),

  /**
   *
   */
  onAttachmentUpload: PropTypes.func.isRequired,

  /**
   *
   */
  onAttachmentRemove: PropTypes.func.isRequired,

  /**
   *
   */
  onSubmit: PropTypes.func.isRequired,
};

ContactForm.defaultProps = {
  attachments: [],
  initialValues: {},
};

export default ContactForm;
